import { StatusBadge } from "../ManagementPage/ManagementPageHTML";
import EntityLabel from "../EntityLabel";
import EditorSection from "../EditorSection";
import { errorMessage } from "../../utils/ErrorMessage";
import { useUnitName } from "../UnitNames";
import { useEffect, useState } from "react";
import { Button, Dialog } from "@akin2unde/flowui-react";
import type { Sale } from "../../models/Sale";
import type { Purchase } from "../../models/Purchase";
import type { ReturnHistory } from "../../models/ReturnHistory";
import { ReturnService } from "../../services/ReturnService";
import { actionStatus, approvedQuantity, returnableQuantity, returnValue } from "../../utils/ReturnMath";
import { useAuth } from "../../auth/useAuth";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import { useInventoryLookups } from "../../hooks/useInventoryLookups";
import "./Returns.css";
type Draft = {
    ProductCode: string;
    Code?: string;
    ReturnQuantity: number;
    ReturnReason: string;
    AdditionalInfo: string;
    IsReSalable: boolean;
    WareHouse: string;
};
export default function ReturnDialog({ kind, code, onClose, onChanged }: {
    kind: "sale" | "purchase";
    code: string;
    onClose: () => void;
    onChanged: () => void;
}) {
    const [record, setRecord] = useState<Sale | Purchase>();
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [refund, setRefund] = useState(0);
    const { hasPermission } = useAuth();
    const { Warehouse, Warehouses } = useWarehouse();
    const { products } = useInventoryLookups();
    const unitName = useUnitName();
    const page = kind === "sale" ? "Sale" : "Purchase";
    const canRequest = hasPermission(`${page}.Return`);
    const canApprove = hasPermission(`${page}.ApproveReturn`);
    const service = ReturnService(kind);
    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const next = await service.Get(code);
            if (next.WareHouse !== Warehouse?.Code)
                throw Error("This transaction does not belong to the operating warehouse.");
            setRecord(next);
        }
        catch (e) {
            setRecord(undefined);
            setError(errorMessage(e, "Could not load returns."));
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => { void load(); }, [code]);
    const name = (productCode: string) => String(products.find(x => x.Key === productCode)?.Value || productCode);
    const histories = (record?.Items ?? []).flatMap(line => (line.ReturnHistories ?? []).map(history => ({ line, history })));
    const change = (index: number, patch: Partial<Draft>) => setDrafts(rows => rows.map((r, i) => i === index ? { ...r, ...patch } : r));
    const build = (productCode: string, history?: ReturnHistory): Draft => ({ ProductCode: productCode, Code: history?.Code, ReturnQuantity: history?.ReturnQuantity ?? 0, ReturnReason: history?.ReturnReason ?? "", AdditionalInfo: history?.AdditionalInfo ?? "", IsReSalable: history?.IsReSalable ?? true, WareHouse: history?.WareHouse || record?.WareHouse || "" });
    const edit = (history: ReturnHistory) => {
        if (actionStatus(history.Status) === "Approve" || !canRequest || busy)
            return;
        setDrafts([build(history.ProductCode, history)]);
        setError("");
        setMessage("");
    };
    const returnAll = () => {
        if (!record)
            return;
        setDrafts(record.Items.filter(line => returnableQuantity(line) > 0).map(line => ({ ...build(line.ProductCode), ReturnQuantity: returnableQuantity(line) })));
        setError("");
        setMessage("");
    };
    const submit = async () => {
        if (busy || !record || !canRequest)
            return;
        if (drafts.some(d => !Number.isFinite(d.ReturnQuantity) || d.ReturnQuantity < 0)) {
            setError("Return quantities cannot be negative or invalid.");
            return;
        }
        const requests = drafts.filter(d => d.ReturnQuantity > 0);
        if (!requests.length) {
            setError("Enter a return quantity for at least one product.");
            return;
        }
        if (new Set(requests.map(x => x.ProductCode)).size !== requests.length) {
            setError("Select each product only once.");
            return;
        }
        for (const d of requests) {
            const matches = record.Items.filter(l => l.ProductCode === d.ProductCode);
            const line = matches[0];
            if (matches.length !== 1) {
                setError("This product occurs on multiple lines. The backend needs a line identifier before it can be returned safely.");
                return;
            }
            if (!Number.isFinite(d.ReturnQuantity) || d.ReturnQuantity > returnableQuantity(line, d.Code)) {
                setError(`Return quantity for ${name(d.ProductCode)} exceeds the remaining quantity.`);
                return;
            }
            if (kind === "sale" && (!d.ReturnReason.trim() || !Warehouses.some(w => w.Code === d.WareHouse))) {
                setError("Enter a reason and receiving warehouse for every sale return.");
                return;
            }
        }
        setBusy(true);
        setError("");
        try {
            await service.Save(code, requests.map(d => kind === "sale" ? { ...d, Code: d.Code || "" } : { ProductCode: d.ProductCode, Code: d.Code || "", ReturnQuantity: d.ReturnQuantity }));
            setDrafts([]);
            setMessage("Return requests saved. Approval is required before stock changes.");
            await load();
            onChanged();
        }
        catch (e) {
            setError(errorMessage(e, "Could not save return requests."));
        }
        finally {
            setBusy(false);
        }
    };
    const decide = async (approve: boolean) => {
        if (busy || !record || !canApprove)
            return;
        const requests = histories.map(x => x.history).filter(h => selected.includes(h.Code) && actionStatus(h.Status) === "Pending");
        if (!requests.length) {
            setError("Select pending requests to approve or reject.");
            return;
        }
        const value = requests.reduce((s, h) => s + Number(h.Amount || 0), 0);
        if (approve && kind === "purchase" && (!Number.isFinite(refund) || refund < 0 || refund > value)) {
            setError("Refund received must be between zero and the selected return value.");
            return;
        }
        if (!window.confirm(`${approve ? "Approve" : "Reject"} ${requests.length} return request(s)?${approve ? " This posts the stock and financial effects defined by the backend." : " Stock will remain unchanged."}`))
            return;
        setBusy(true);
        setError("");
        try {
            await service.Decide(code, requests, approve, approve ? refund : 0);
            setSelected([]);
            setRefund(0);
            setMessage(`Return requests ${approve ? "approved" : "rejected"}.`);
            await load();
            onChanged();
        }
        catch (e) {
            setError(errorMessage(e, "Could not action returns."));
        }
        finally {
            setBusy(false);
        }
    };
    const money = (amount: number) => new Intl.NumberFormat("en-NG", { style: "currency", currency: record?.CurrencyCode || "NGN" }).format(amount);
    return <Dialog open position="right" width="1040px" maxWidth="full" showCloseButton closeOnEscape={!busy} closeOnOverlayClick={!busy} onClose={() => {
            if (!busy)
                onClose();
        }} header={<div><h2>{kind === "sale" ? "Sales return" : "Purchase return"}</h2><p>{code} · Original units and conversions are retained.</p></div>} body={<div className="returns-panel">
    {loading ? <p role="status">Loading return requests…</p> : record && <>
    <div className="return-payment"><span>{kind === "sale" ? "Sale" : "Purchase"} payment</span><StatusBadge Status={record.PaymentStatus}/></div>
    <EditorSection className="return-section" title={<>Products</>}><p>Return all fills the original quantity for untouched lines. Approved and pending quantities are deducted from the amount still available.</p><div className="return-toolbar">{canRequest && <Button disabled={busy} variant="outline" onClick={returnAll}>Return all available</Button>}</div>
    <div className="return-lines">{record.Items.map((line, i) => <article key={i}><strong><EntityLabel entity="product" code={line.ProductCode}/></strong><div className="return-payment"><span>Payment</span><StatusBadge Status={"PaymentStatus" in line ? String(line.PaymentStatus) : record.PaymentStatus}/></div><p>Original: {line.Quantity} {unitName(line.UnitCode)} · Approved: {approvedQuantity(line)} · Available to request: {returnableQuantity(line)}</p><small>1 unit = {line.BaseUnitQuantity} base stock units</small>{canRequest && <Button variant="outline" disabled={busy || returnableQuantity(line) <= 0} onClick={() => { setDrafts([build(line.ProductCode)]); setError(""); }}>Request return</Button>}</article>)}</div></EditorSection>
    {drafts.length > 0 && <EditorSection className="return-section" title={<>{drafts.some(d => d.Code) ? "Edit return request" : "Initiate returns"}</>}>{drafts.map((d, i) => { const line = record.Items.find(l => l.ProductCode === d.ProductCode)!; const max = returnableQuantity(line, d.Code); return <EditorSection key={d.ProductCode} className="admin-section line-section" title={<>{name(d.ProductCode)}</>}><div className="line-fields"><div className="return-form-grid"><label>Return quantity ({unitName(line.UnitCode)})<input type="number" min="0" max={max} step="any" value={d.ReturnQuantity} disabled={busy} onChange={e => change(i, { ReturnQuantity: Number(e.target.value) })}/><small>Maximum {max} · {d.ReturnQuantity * line.BaseUnitQuantity} base units</small></label><div className="return-value"><span>Estimated return value</span><strong>{money(d.ReturnQuantity > 0 ? returnValue(line, d.ReturnQuantity) : 0)}</strong><small>Original net value after discounts; the server confirms the final amount.</small></div>{kind === "sale" && <><label>Receiving warehouse<select value={d.WareHouse} disabled={busy} onChange={e => change(i, { WareHouse: e.target.value })}><option value="">Select warehouse</option>{Warehouses.map(w => <option key={w.Code} value={w.Code}>{w.Name}</option>)}</select></label><label>Disposition<select value={d.IsReSalable ? "restock" : "damage"} disabled={busy} onChange={e => change(i, { IsReSalable: e.target.value === "restock" })}><option value="restock">Resalable — add to stock</option><option value="damage">Not resalable — record damage</option></select></label><label className="return-full">Reason<textarea value={d.ReturnReason} disabled={busy} onChange={e => change(i, { ReturnReason: e.target.value })}/></label><label className="return-full">Additional information<textarea value={d.AdditionalInfo} disabled={busy} onChange={e => change(i, { AdditionalInfo: e.target.value })}/></label></>}</div></div></EditorSection>; })}<div className="return-edit-actions"><Button disabled={busy} onClick={() => void submit()}>{busy ? "Saving…" : "Save return requests"}</Button><Button disabled={busy} variant="outline" onClick={() => setDrafts([])}>Cancel edit</Button></div></EditorSection>}
    <EditorSection className="return-section" title={<>Return history</>}>{!histories.length ? <p>No return requests yet.</p> : histories.map(({ line, history: h }, i) => <article className="return-history" key={h.Code || i}>{canApprove && actionStatus(h.Status) === "Pending" && <input type="checkbox" aria-label={`Select return for ${name(h.ProductCode)}`} checked={selected.includes(h.Code)} disabled={busy || !h.Code} onChange={e => setSelected(s => e.target.checked ? [...s, h.Code] : s.filter(c => c !== h.Code))}/>}<div><strong><EntityLabel entity="product" code={h.ProductCode}/></strong><div className="return-payment"><span>Payment</span><StatusBadge Status={"PaymentStatus" in line ? String(line.PaymentStatus) : record.PaymentStatus}/></div><p>{h.ReturnQuantity} {unitName(line.UnitCode)} · {money(h.Amount)} · {actionStatus(h.Status) === "Approve" ? "Approved" : actionStatus(h.Status) === "Decline" ? "Rejected" : "Pending"}</p>{h.ReturnReason && <p>{h.ReturnReason}</p>}{h.ActionedBy && <small>Actioned by <EntityLabel entity="staff" code={h.ActionedBy}/></small>}</div>{canRequest && actionStatus(h.Status) !== "Approve" && <Button variant="outline" disabled={busy || !h.Code} onClick={() => edit(h)}>Edit request</Button>}</article>)}
    {canApprove && histories.some(x => actionStatus(x.history.Status) === "Pending") && <div className="return-decision">{kind === "purchase" && <label>Supplier refund received<input type="number" min="0" step="0.01" value={refund} disabled={busy} onChange={e => setRefund(Number(e.target.value))}/><small>Leave zero if no money was received.</small></label>}<Button disabled={busy || !selected.length} onClick={() => void decide(true)}>Approve selected</Button><Button variant="outline" disabled={busy || !selected.length} onClick={() => void decide(false)}>Reject selected</Button></div>}</EditorSection>
    </>}{error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}{!record && !loading && <Button onClick={() => void load()}>Retry</Button>}</div>} footer={<Button variant="outline" disabled={busy} onClick={onClose}>Close</Button>}/>;
}
